import { useState } from "react";
import { 
  Heart, 
  Brain, 
  Briefcase, 
  HandHeart, 
  Shield, 
  Smile,
  Lock
} from "lucide-react";

const toneOptions = [
  {
    id: "kind",
    label: "Kind & Caring",
    description: "Warm, empathetic, and emotionally supportive",
    icon: Heart
  },
  {
    id: "calm",
    label: "Calm & Logical",
    description: "Clear, composed, and rational without emotional intensity",
    icon: Brain
  },
  {
    id: "professional",
    label: "Professional",
    description: "Neutral, respectful, and appropriate for work or formal situations",
    icon: Briefcase
  },
  {
    id: "apologetic",
    label: "Apologetic",
    description: "Sincere, accountable, and focused on repairing trust",
    icon: HandHeart
  },
  {
    id: "boundary",
    label: "Boundary-Setting",
    description: "Firm, respectful, and clear about limits without being hostile",
    icon: Shield
  },
  {
    id: "friendly",
    label: "Light & Friendly",
    description: "Casual, relaxed, and positive to keep things easygoing",
    icon: Smile
  }
];

export default function ToneSelector({ 
  selectedTone, 
  onSelectTone, 
  disabled = false,
  freeTones = null,  // null = all unlocked, array = only these are free
  onLockedClick = null  // callback when locked tone is clicked
}) {
  // If freeTones is null or undefined, all tones are available
  const allUnlocked = freeTones === null || freeTones === undefined;

  const handleToneClick = (tone) => {
    if (disabled) return;
    
    const isLocked = !allUnlocked && !freeTones.includes(tone.id);
    
    if (isLocked) {
      onLockedClick?.();
      return;
    }
    
    onSelectTone(tone.id);
  };

  return (
    <div className="w-full" data-testid="tone-selector">
      {/* Header */}
      <div className="mb-5">
        <h3 className="text-lg font-semibold text-[#1F2937] mb-1">
          Choose how you want to sound
        </h3>
        <p className="text-sm text-[#6B7280]">
          We'll adjust the message to match your tone.
        </p>
      </div>

      {/* Tone Options Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {toneOptions.map((tone) => {
          const Icon = tone.icon;
          const isSelected = selectedTone === tone.id;
          const isLocked = !allUnlocked && !freeTones.includes(tone.id);
          
          return (
            <button
              key={tone.id}
              onClick={() => handleToneClick(tone)}
              disabled={disabled}
              data-testid={`tone-${tone.id}`}
              data-locked={isLocked}
              className={`
                relative flex items-start gap-3 p-4 rounded-2xl text-left
                transition-all duration-200 ease-out
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                ${isLocked 
                  ? 'bg-gray-50 text-gray-400 border-2 border-dashed border-gray-200' 
                  : isSelected 
                    ? 'bg-[#2F5BFF] text-white shadow-lg scale-[1.02]' 
                    : 'bg-[#F3F4F6] text-[#1F2937] hover:bg-[#E5E7EB] hover:scale-[1.01]'
                }
              `}
            >
              {/* Lock icon for locked tones */}
              {isLocked && (
                <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                  <Lock size={12} className="text-gray-500" />
                </div>
              )}

              {/* Icon */}
              <div className={`
                flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center
                ${isLocked 
                  ? 'bg-gray-100' 
                  : isSelected 
                    ? 'bg-white/20' 
                    : 'bg-white'
                }
              `}>
                <Icon 
                  size={20} 
                  className={isLocked ? 'text-gray-400' : isSelected ? 'text-white' : 'text-[#2F5BFF]'} 
                />
              </div>
              
              {/* Text */}
              <div className="flex-1 min-w-0">
                <div className={`font-semibold text-base mb-0.5 flex items-center gap-2
                  ${isLocked ? 'text-gray-400' : isSelected ? 'text-white' : 'text-[#1F2937]'}
                `}>
                  {tone.label}
                  {isLocked && (
                    <span className="text-xs font-normal bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">
                      Premium
                    </span>
                  )}
                </div>
                <div className={`text-sm leading-snug 
                  ${isLocked ? 'text-gray-400' : isSelected ? 'text-white/80' : 'text-[#6B7280]'}
                `}>
                  {tone.description}
                </div>
              </div>

              {/* Selected indicator */}
              {isSelected && !isLocked && (
                <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-white flex items-center justify-center">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="#2F5BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { toneOptions };
